// Live per-incident presence ("who's viewing this call") + typing indicators,
// carried over the shared realtime WebSocket. Presence is heartbeat-based so it
// self-heals on disconnect (entries expire when heartbeats stop).
import { useEffect, useRef, useState, useCallback } from "react";
import { sendRealtime, useRealtimeChannel, CLIENT_ID } from "@/lib/realtime";

export function useCallPresence({ cc, label, active = true }) {
  const [viewers, setViewers] = useState([]);
  const [typers, setTypers] = useState([]);
  const store = useRef({ viewers: {}, typers: {} });
  const lastType = useRef(0);

  useRealtimeChannel(useCallback((msg) => {
    if (!cc || msg.cc !== cc || msg.cid === CLIENT_ID) return;
    const key = msg.cid || msg.who;
    if (!key) return;
    const lbl = msg.label || msg.who || "User";
    const now = Date.now();
    if (msg.type === "presence") {
      if (msg.action === "leave") delete store.current.viewers[key];
      else store.current.viewers[key] = { label: lbl, ts: now };
    } else if (msg.type === "typing") {
      store.current.typers[key] = { label: lbl, ts: now };
    }
  }, [cc]));

  useEffect(() => {
    if (!cc || !active) return;
    const beat = () => sendRealtime({ type: "presence", action: "enter", cc, label });
    beat();
    const hb = setInterval(beat, 4000);
    const sweep = setInterval(() => {
      const now = Date.now();
      const v = store.current.viewers, t = store.current.typers;
      Object.keys(v).forEach((k) => { if (now - v[k].ts > 10000) delete v[k]; });
      Object.keys(t).forEach((k) => { if (now - t[k].ts > 3000) delete t[k]; });
      setViewers([...new Set(Object.values(v).map((x) => x.label))]);
      setTypers([...new Set(Object.values(t).map((x) => x.label))]);
    }, 1000);
    return () => {
      clearInterval(hb); clearInterval(sweep);
      sendRealtime({ type: "presence", action: "leave", cc, label });
    };
  }, [cc, active, label]);

  const notifyTyping = useCallback(() => {
    if (!cc) return;
    const now = Date.now();
    if (now - lastType.current < 1200) return;
    lastType.current = now;
    sendRealtime({ type: "typing", cc, label });
  }, [cc, label]);

  return { viewers, typers, notifyTyping };
}
